import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Font } from '@react-pdf/renderer';
import customFont from './Monotype-Corsiva-Bold-Italic.ttf';
import MainFont from './booksold.ttf';
import MainFontBold from './Bookman Old Style Bold.ttf';
import certLogo from './certlogo.png';
import signatureImg from './signature.png';

// Register fonts
Font.register({
  family: 'Monotype-Corsiva-Bold-Italic',
  src: customFont,
});

Font.register({
  family: 'Booksold',
  src: MainFont,
});

Font.register({
  family: 'BooksoldBold',
  src: MainFontBold,
});

const styles = StyleSheet.create({
  light: { fontWeight: 300 },
  normal: { fontWeight: 400 },
  bold: { fontWeight: 700 },
  extraBold: { fontWeight: 900 },

  page: {
    backgroundColor: '#ffffff',
    padding: 40,
  },

  header: {
    textAlign: 'right',
    fontSize: 12,
    marginBottom: 10,
    fontFamily: 'Booksold',
  },

  certNo: {
    fontFamily: 'BooksoldBold',
    textDecoration: 'underline',
    marginBottom: 15,
  },

  titleContainer: {
    alignItems: 'center',
    marginTop: 10,
    marginBottom: 10,
  },

  logo: {
    width: 200,
    height: 'auto',
  },

  republic: {
    marginTop: 8,
    fontSize: 12,
    fontFamily: 'BooksoldBold',
  },

  board: {
    fontSize: 12,
    fontFamily: 'BooksoldBold',
  },

  divider: {
    marginTop: 5,
    width: 200,
    height: 2.5,
    backgroundColor: '#000000',
  },

  mainTitle: {
    fontSize: 20,
    textTransform: 'uppercase',
    textAlign: 'center',
    marginVertical: 10,
    fontFamily: 'BooksoldBold',
  },

  mainContent: {
    textAlign: 'center',
    marginBottom: 30,
    fontSize: 12,
  },

  name: {
    fontSize: 30,
    marginTop: 10,
    fontFamily: 'BooksoldBold',
  },

  licenseText: {
    marginTop: 10,
    lineHeight: 1.5,
  },

  position: {
    fontSize: 24,
    fontWeight: 900,
    textTransform: 'uppercase',
    marginVertical: 10,
    fontFamily: 'Monotype-Corsiva-Bold-Italic',
  },

  dateText: {
    marginBottom: 15,
    lineHeight: 1.5,
  },

  signatureSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingTop: 30,
  },

  signatureContainer: {
    alignItems: 'center',
  },

  signature: {
    width: 50,
    height: 'auto',
  },

  signatureLabel: {
    borderTop: '2px dashed #000000',
    paddingTop: 5,
    width: 150,
    textAlign: 'center',
    fontSize: 12,
  },

  qrCodeContainer: {
    alignItems: 'center',
    marginTop: 10,
  },

  qrCode: {
    width: 72,
    height: 72,
  },

  qrLabel: {
    fontSize: 10,
    marginBottom: 3,
  },

  footer: {
    marginTop: 32,
    textAlign: 'center',
    borderTop: '1px dashed #cccccc',
    paddingTop: 10,
    fontSize: 10,
    color: '#555555',
  },

  footerContact: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 3,
  },

  contactText: {
    fontSize: 8,
    marginHorizontal: 5,
  },

  link: {
    color: 'blue',
    textDecoration: 'underline',
  },

  issueDate: {
    fontSize: 10,
    marginTop: 1,
    marginBottom: 2,
  },
});

const CertificateDocument = ({
  certificateNumber,
  name,
  licenseNo,
  approvedDate,
  signature,
  qrCodeImage,
  year,
}) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View>
          {/* Header */}
          <Text style={styles.header}>
            CERTIFICATE NO:{' '}
            <Text style={styles.certNo}>
              {certificateNumber || licenseNo}
            </Text>
          </Text>

          {/* Title Section */}
          <View style={styles.titleContainer}>
            <Image style={styles.logo} src={certLogo} />
            <Text style={styles.republic}>REPUBLIC OF KENYA</Text>
            <Text style={styles.board}>LAND SURVEYORS' BOARD</Text>
            <View style={styles.divider} />
          </View>

          <Text style={styles.mainTitle}>Annual Practicing Certificate</Text>

          {/* Main Content */}
          <View style={styles.mainContent}>
            <Text>This is to certify that</Text>

            <Text style={styles.name}>{name}</Text>

            <Text style={styles.licenseText}>
              LICENSE NO. <Text style={{ fontWeight: 'bold' }}>{licenseNo}</Text>
              {'\n'}is authorized to practice in the Republic of Kenya as a
            </Text>

            <Text style={styles.position}>Licensed Surveyor</Text>

            <Text style={styles.dateText}>
              for the period from{' '}
              <Text style={{ fontWeight: 900 }}>
                {approvedDate
                  ? new Date(approvedDate).toLocaleDateString('en-KE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                  : ''}
              </Text>{' '}
              to <Text style={{ fontWeight: 900 }}>31 December {year}</Text>,
              {'\n'}in accordance with the Survey Act (Cap 299, Laws of Kenya) and
              its regulations.
            </Text>
          </View>

          {/* Signatures */}
          <View style={styles.signatureSection}>
            <View style={styles.signatureContainer}>
              <Image style={styles.signature} src={signatureImg} />
              <Text style={styles.signatureLabel}>Secretary to the Board</Text>
            </View>

            <View style={styles.signatureContainer}>
              <Text style={styles.issueDate}>
                {approvedDate
                  ? new Date(approvedDate).toLocaleDateString('en-KE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                  : ''}
              </Text>
              <Text style={styles.signatureLabel}>Date of Issue</Text>
            </View>
          </View>

          {/* QR Code */}
          <View style={styles.qrCodeContainer}>
            {qrCodeImage && <Image style={styles.qrCode} src={qrCodeImage} />}
            <Text style={styles.qrLabel}>Scan to verify.</Text>
            <Text style={{ fontSize: 6, marginTop: 2 }}>
              {signature || 'No Signature'}
            </Text>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Text>
              This Certificate is system generated and can be verified by
              scanning the QR Code.
            </Text>

            <View style={styles.footerContact}>
              <Text style={styles.contactText}>
                Email: <Text style={styles.link}>info@lsb.go.ke</Text>
              </Text>

              <Text style={styles.contactText}>
                Website: <Text style={styles.link}>www.lsb.go.ke</Text>
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default CertificateDocument;